import { useState, useEffect, useCallback, useRef } from 'react';
import { currencyAPI } from '../services/api';

interface CurrencyRate {
  from: string;
  to: string;
  rate: number;
  timestamp: number;
}

interface CurrencyCache {
  [key: string]: CurrencyRate;
}

const CACHE_EXPIRY = 3600000; // 1 hour in milliseconds
const cache: CurrencyCache = {};

const useCurrency = (defaultCurrency: string = 'USD') => {
  const [rates, setRates] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef(cache);

  const fetchRates = useCallback(async (baseCurrency: string = defaultCurrency) => {
    setLoading(true);
    setError(null);
    try {
      const response = await currencyAPI.getRates(baseCurrency);
      setRates(response.data.rates || {});
      
      // Cache the rates both ways for conversion
      Object.keys(response.data.rates || {}).forEach((to) => {
        const rate = response.data.rates[to];
        const key = `${baseCurrency}_${to}`;
        const inverseKey = `${to}_${baseCurrency}`;
        
        cacheRef.current[key] = {
          from: baseCurrency,
          to,
          rate: rate,
          timestamp: Date.now(),
        };
        
        // Also cache the inverse rate
        if (rate !== 0) {
          cacheRef.current[inverseKey] = {
            from: to,
            to: baseCurrency,
            rate: 1 / rate,
            timestamp: Date.now(),
          };
        }
      });
    } catch (err) {
      console.error('Failed to fetch currency rates:', err);
      setError('Failed to fetch currency rates');
    } finally {
      setLoading(false);
    }
  }, [defaultCurrency]);

  const convert = useCallback((amount: number, from: string, to: string): number | null => {
    if (from === to) return amount;
    
    // Check cache first
    const cacheKey = `${from}_${to}`;
    const cached = cacheRef.current[cacheKey];
    
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
      return amount * cached.rate;
    }
    
    // Try to find via rates object (if we have the base currency rates)
    // If 'from' is in rates (meaning defaultCurrency is base) we need inverse
    // If 'to' is in rates, we can use directly
    if (rates[from] && rates[to]) {
      // Cross conversion: amount / rate[from] * rate[to]
      const baseAmount = amount / rates[from]; // convert to base
      return baseAmount * rates[to]; // convert to target
    }
    
    if (rates[to] && !rates[from]) {
      // 'from' is the base currency
      return amount * rates[to];
    }
    
    if (rates[from] && !rates[to]) {
      // 'to' is the base currency
      return amount / rates[from];
    }
    
    // Return null if rate not available
    return null;
  }, [rates]);

  const getCurrencySymbol = useCallback((code: string): string => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      AUD: 'A$',
      CAD: 'C$',
      CHF: '₣',
      SEK: 'kr',
      NZD: 'NZ$',
      MXN: '$',
      SGD: 'S$',
      HKD: 'HK$',
      NOK: 'kr',
      KRW: '₩',
      TRY: '₺',
      RUB: '₽',
      BRL: 'R$',
      ZAR: 'R',
      AED: 'د.إ',
      IDR: 'Rp',
      MYR: 'RM',
      PHP: '₱',
      THB: '฿',
      VND: '₫',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      RON: 'lei',
      HRK: 'kn',
      BGN: 'лв',
      ISK: 'kr',
      DKK: 'kr',
    };
    return symbols[code] || code;
  }, []);

  const formatPrice = useCallback((
    amount: number,
    currency: string,
    targetCurrency?: string
  ): string => {
    if (!amount) return '—';
    
    const displayCurrency = targetCurrency || defaultCurrency;
    const converted = targetCurrency && targetCurrency !== currency 
      ? convert(amount, currency, targetCurrency)
      : amount;

    if (converted === null && targetCurrency && targetCurrency !== currency) {
      return `${amount.toFixed(2)} ${currency} → ${targetCurrency}`;
    }

    const displayAmount = converted ?? amount;
    const currencySymbol = getCurrencySymbol(displayCurrency);

    // Compact formatting for large numbers to prevent layout breaking
    if (displayAmount >= 1_000_000_000) {
      return `${currencySymbol}${(displayAmount / 1_000_000_000).toFixed(1)}B`;
    }
    if (displayAmount >= 1_000_000) {
      return `${currencySymbol}${(displayAmount / 1_000_000).toFixed(1)}M`;
    }
    if (displayAmount >= 10_000) {
      return `${currencySymbol}${Math.round(displayAmount).toLocaleString()}`;
    }
    
    return `${currencySymbol}${displayAmount.toFixed(2)}`;
  }, [defaultCurrency, convert, getCurrencySymbol]);

  useEffect(() => {
    fetchRates(defaultCurrency);
  }, [defaultCurrency, fetchRates]);

  return {
    rates,
    loading,
    error,
    convert,
    formatPrice,
    getCurrencySymbol,
    fetchRates,
  };
};

export default useCurrency;
